import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"

function normalizePlaceIdsInput(placeIds) {
  const ids = Array.isArray(placeIds) ? placeIds : [placeIds].filter(Boolean)
  return [...new Set(ids.map((id) => String(id).trim()).filter(Boolean))]
}

function idsMissingFromCache(uniqueIds, byId) {
  return uniqueIds.filter((id) => {
    const row = byId?.[String(id)]
    return !row || !String(row.name ?? "").trim()
  })
}

function normalizePlacesResponsePayload(data) {
  if (data == null) return []
  if (Array.isArray(data)) return data
  const inner = data?.data ?? data?.places ?? data?.content ?? data?.results
  if (Array.isArray(inner)) return inner
  if (inner && typeof inner === "object") return [inner]
  return []
}

/** Pick stable string id + display name from one API row (handles alternate field names). */
function normalizePlaceRow(place) {
  if (!place || typeof place !== "object") return null
  const rawId =
    place.id ??
    place.placeId ??
    place.place_id ??
    place.facebookPlaceId ??
    place.facebook_place_id
  if (rawId == null || String(rawId).trim() === "") return null
  const id = String(rawId).trim()
  const nameRaw =
    place.name ??
    place.title ??
    place.placeName ??
    place.place_name ??
    place.location?.name ??
    ""
  const name = String(nameRaw).trim()
  const link = place.link != null && String(place.link).trim() !== "" ? String(place.link).trim() : ""
  return { id, name, link, location: place.location }
}

/**
 * Fetch Facebook place details by IDs (one GET with repeated placeIds query params).
 * Skips IDs already in store with a non-empty name. Coalesces duplicate dispatches via condition.
 */
export const fetchFacebookPlaces = createAsyncThunk(
  "facebookPlaces/fetch",
  async (placeIds, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token
      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }
      const uniqueIds = normalizePlaceIdsInput(placeIds)
      const byId = state.facebookPlaces?.byId ?? {}
      const missingIds = idsMissingFromCache(uniqueIds, byId)
      if (missingIds.length === 0) return []

      const params = new URLSearchParams()
      missingIds.forEach((id) => params.append("placeIds", id))
      const url = `facebook/places?${params.toString()}`
      const data = await get(url)

      const list = normalizePlacesResponsePayload(data)
      const normalized = list.map(normalizePlaceRow).filter(Boolean)
      return normalized
    } catch (err) {
      console.error("Facebook places fetch error:", err)
      return rejectWithValue(err?.message || "Failed to fetch places")
    }
  },
  {
    condition: (placeIds, { getState }) => {
      const uniqueIds = normalizePlaceIdsInput(placeIds)
      if (uniqueIds.length === 0) return false
      const byId = getState().facebookPlaces?.byId ?? {}
      return idsMissingFromCache(uniqueIds, byId).length > 0
    },
  },
)

const initialState = {
  /** @type {Record<string, { id: string, name: string, link: string, location?: object }>} */
  byId: {},
  status: "idle",
  error: null,
}

const facebookPlacesSlice = createSlice({
  name: "facebookPlaces",
  initialState,
  reducers: {
    clearFacebookPlaces: (state) => {
      state.byId = {}
      state.status = "idle"
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFacebookPlaces.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchFacebookPlaces.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.error = null
        const list = Array.isArray(action.payload) ? action.payload : []
        list.forEach((place) => {
          const row = normalizePlaceRow(place) ?? place
          if (!row?.id) return
          const id = String(row.id)
          state.byId[id] = {
            id,
            name: row.name ?? "",
            link: row.link ?? "",
            location: row.location,
          }
        })
      })
      .addCase(fetchFacebookPlaces.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to fetch places"
      })
  },
})

export const { clearFacebookPlaces } = facebookPlacesSlice.actions

/** Select place by id */
export const selectPlaceById = (state, id) => {
  if (id == null || id === "") return null
  return state.facebookPlaces?.byId?.[String(id)] ?? null
}

export default facebookPlacesSlice.reducer
